import type {Event} from '@/apiTypes/event';
import {ShareButton} from '@/components/shareButton/shareButton';
import {Button} from '@/components/ui/button';
import {getRouteApi} from '@tanstack/react-router';
import {useEffect, useRef, useState} from 'react';
import {Sheet, SheetRef} from 'react-modal-sheet';
import {ActionRow} from '../registrationPanel/actionRow';
import {RegistrationPanel} from '../registrationPanel/registrationPanel';

const routeApi = getRouteApi('/event/$eventId/');

/**
 * Mobile footer for event registration. Inlines `<RegistrationPanel />` for
 * every case it already handles well (Registered state, admin "Your Event",
 * external Track + register link, simple native one-click register). Only
 * when the user would actually have to choose a route/category does it swap
 * in a bare "Register" button that opens a sheet containing the same panel.
 *
 * The sheet stays open through submit (so "Registering..." shows there) and
 * auto-closes once `isRegistered` flips true after the loader refresh.
 */
export const MobileRegistration = () => {
  const {
    event,
    isAdmin,
    registrationStatus,
    attendanceStatus,
    membershipStatus,
  } = routeApi.useLoaderData();
  const sheetRef = useRef<SheetRef | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Any non-actionable registration state (approved, awaiting organizer
  // review, or denied) — the panel handles each of these with an inline
  // terminal button, so the sheet adds nothing.
  const pendingOrDenied =
    registrationStatus.registration?.status === 'PENDING_PREREQUISITE_REVIEW' ||
    registrationStatus.registration?.status === 'DENIED';
  const hasTerminalRegistrationState =
    registrationStatus.isRegistered || pendingOrDenied;

  useEffect(() => {
    if (hasTerminalRegistrationState) setIsOpen(false);
  }, [hasTerminalRegistrationState]);

  if (!event) return null;

  const isMembersOnlyBlocked =
    event.requiresClubMembership === true && !membershipStatus.isMember;

  const needsSelectionSheet =
    !event.sourceUrl &&
    !isAdmin &&
    !hasTerminalRegistrationState &&
    !isMembersOnlyBlocked &&
    !isSimpleNativeEvent(event);

  const shareButton = (
    <ShareButton
      title={event.name}
      contentType="event"
      contentId={event.id}
      size="lg"
      iconOnly
    />
  );

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 py-3 lg:hidden space-y-2 gap-3 p-6">
      {needsSelectionSheet ? (
        <ActionRow accessory={shareButton}>
          <Button size="lg" className="w-full" onClick={() => setIsOpen(true)}>
            Register
          </Button>
        </ActionRow>
      ) : (
        <RegistrationPanel
          event={event}
          isTracking={attendanceStatus.isAttending}
          registrationStatus={registrationStatus}
          isAdmin={isAdmin}
          isMember={membershipStatus.isMember}
          actionAccessory={shareButton}
        />
      )}
      <Sheet
        ref={sheetRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        detent="content"
      >
        <Sheet.Container>
          <Sheet.Header />
          <Sheet.Content>
            <div className="px-6 pb-6">
              <RegistrationPanel
                event={event}
                isTracking={attendanceStatus.isAttending}
                registrationStatus={registrationStatus}
                isAdmin={isAdmin}
                isMember={membershipStatus.isMember}
              />
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setIsOpen(false)} />
      </Sheet>
    </div>
  );
};

/**
 * A native event is "simple" when the user has no meaningful choice during
 * registration — at most one route, and that route has at most one category.
 * For these, `<NativeRegistrationPanel />` renders just a Register button
 * inline, so the sheet adds nothing.
 */
function isSimpleNativeEvent(event: Event): boolean {
  const routes = event.routes ?? [];
  if (routes.length > 1) return false;
  return routes.every((route) => (route.categories?.length ?? 0) <= 1);
}
